import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
// Plugins
import { Observable, of } from 'rxjs';
import { NzTableQueryParams } from 'ng-zorro-antd/table';
import { SessionStorage } from 'ngx-webstorage';
// Models
import { Response } from '@models/api/api.model';
import { AuthData } from '@models/auth/auth.model.ro';
import { BusinessAdminUsersDTO, BusinessUsersDTO, SaveBusinessUsersDTO, ResetPasswordDTO, SaveUserDTO, TempPasswordDTO } from '@models/user/user.model.dto';
import { BusinessAdminUsersRO, BusinessUsersRO, TempPasswordRO, UsersRO } from '@models/user/user.model.ro';
import { BusinessUserSaveData, User, UserFormControls } from '@models/user/user.model';
// Shared
import { APP_HOST, ENDPOINTS } from '@shared/endpoints.enum';
import { CustomColumn } from '@shared/interfaces';
// Services
import { HttpService } from '@services/http/http.service';

@Injectable({
	providedIn: 'root'
})
export class UserService {

	public userListPreviousParams: string = '';

	@SessionStorage() public authData: AuthData;

	public constructor(private httpServcice: HttpService) { }

	public getBusinessUsers(uuid: string): Observable<BusinessUsersRO> {
		let data: BusinessUsersDTO = { uuid_tParent: uuid };

		return this.httpServcice.request('post', ENDPOINTS.READ_USERS, APP_HOST.PHP, data, false, true);
	}

	public getBusinessAdmins(uuid: string): Observable<BusinessAdminUsersRO> {
		let data: BusinessAdminUsersDTO = { parent_uuid: uuid };

		return this.httpServcice.request('post', ENDPOINTS.LIST_PARENT_USER, APP_HOST.PHP, data, false, true);
	}

	public saveBusinessUsers(uuid: string, saveData: Array<BusinessUserSaveData>): Observable<Response> {
		let data: SaveBusinessUsersDTO = {
			parent_uuid: uuid,
			save_data: saveData
		};

		return this.httpServcice.request('post', ENDPOINTS.SAVE_PARENT_USER, APP_HOST.PHP, data, false, false, true);
	}

	public resetPassword(userId: string, password: string): Observable<Response> {
		let data: ResetPasswordDTO = {
			user_uuid: userId,
			fPassword: password
		};

		return this.httpServcice.request('post', ENDPOINTS.RESET_USER_PASSWORD, APP_HOST.PHP, data, false, false, true);
	}

	public getUsers(params: Partial<NzTableQueryParams>, searchInfo: object, includeDeleted: number = 0): Observable<UsersRO> {
		let httpParams: HttpParams = new HttpParams();

		if (params?.pageIndex) httpParams = httpParams.append('offset', params.pageSize * (params.pageIndex - 1));

		if (params?.pageSize) httpParams = httpParams.append('limit', params.pageSize);

		for (let item of params?.sort) {
			if (!item.value) continue;

			let sortType: number = -1;

			if (item?.value == 'ascend') sortType = 1;

			httpParams = httpParams.append('sort_field', item?.key);
			httpParams = httpParams.append('sort_type', sortType);
		}

		for (let key in searchInfo) {
			if (searchInfo[key].value?.trim() != '') {
				httpParams = httpParams.append(key, searchInfo[key].value?.trim());
			}
		}

		if (!includeDeleted) httpParams = httpParams.append('fDeleted', includeDeleted);

		if (this.userListPreviousParams == httpParams.toString()) return of();

		this.userListPreviousParams = httpParams.toString();

		return this.httpServcice.request('get', ENDPOINTS.USERS, APP_HOST.PY, { params: httpParams }, false, true);
	}

	public saveUser(user: User, formControls: UserFormControls, deleted: number): Observable<Response> {
		let data: SaveUserDTO = {
			user_uuid: user?.uuid,
			fName: `${ formControls.firstName.value } ${ formControls.lastName.value }`,
			staff_username: user?.staff_username,
			fFirstName: formControls.firstName.value,
			fLastName: formControls.lastName.value,
			fAddress: formControls.address.value,
			email_verified: formControls.emailVerified.value ? 1 : 0,
			mobile_verified: formControls.mobileVerified.value ? 1 : 0,
			fDeleted: deleted,
			fLogging: formControls.logging.value ? 1 : 0,
			fFastLoginProcess: formControls.fastLoginProcess.value ? 1 : 0,
			fRedDotAlert: formControls.redDotAlert.value ? 1 : 0,
			fRegistered: formControls.registered.value ? 1 : 0,
			enum_UserType: formControls.userType.value
		};

		if (user?.fOrigin) data.fOrigin = user?.fOrigin;

		return this.httpServcice.request('post', ENDPOINTS.SAVE_PRI_USER, APP_HOST.PHP, data, false, false, true);
	}

	public createTempPassword(userId: string): Observable<TempPasswordRO> {
		let data: TempPasswordDTO = { user_uuid: userId };

		return this.httpServcice.request('post', ENDPOINTS.CREATE_TEMP_PASSWORD, APP_HOST.PHP, data, false, false, false);
	}

	public getTableColumns(): Array<CustomColumn> {
		return [
			{
				name: 'UUID',
				value: 'uuid',
				default: false,
				width: 150,
				fixWidth: true,
				custom: { hasSearch: true, tooltip: true }
			},
			{
				name: 'Username',
				value: 'fUsername',
				default: true,
				width: 150,
				fixWidth: true,
				custom: { hasSearch: true, tooltip: true }
			},
			{
				name: 'Country Code',
				value: 'fCountryCode',
				default: false,
				width: 80,
				fixWidth: true,
				custom: { hasSearch: true }
			},
			{
				name: 'Mobile Number',
				value: 'fMobile',
				default: true,
				width: 100,
				fixWidth: true,
				custom: { hasSearch: true }
			},
			{
				name: 'Name',
				value: 'fName',
				default: true,
				width: 150,
				fixWidth: true,
				sortFn: true,
				custom: { hasSearch: true, tooltip: true }
			},
			{
				name: 'First Name',
				value: 'fFirstName',
				default: true,
				width: 100,
				fixWidth: true,
				sortFn: true,
				custom: { hasSearch: true, tooltip: true }
			},
			{
				name: 'Last Name',
				value: 'fLastName',
				default: true,
				width: 100,
				fixWidth: true,
				sortFn: true,
				custom: { hasSearch: true, tooltip: true }
			},
			{
				name: 'Business Count',
				value: 'user_business_item',
				default: true,
				width: 80,
				fixWidth: true,
				custom: { count: true, changeValue: true }
			},
			{
				name: 'Address',
				value: 'fAddress',
				default: false,
				width: 150,
				fixWidth: true
			},
			{
				name: 'Staff username',
				value: 'staff_username',
				default: false,
				width: 150,
				fixWidth: true
			},
			{
				name: 'Email Verified',
				value: 'email_verified',
				default: false,
				width: 80,
				fixWidth: true,
				custom: { yesNo: true, changeValue: true }
			},
			{
				name: 'Mobile Verified',
				value: 'mobile_verified',
				default: false,
				width: 80,
				fixWidth: true,
				custom: { yesNo: true, changeValue: true }
			},
			{
				name: 'Logging',
				value: 'fLogging',
				default: false,
				width: 80,
				fixWidth: true,
				custom: { yesNo: true, changeValue: true }
			},
			{
				name: 'Fast Login Process',
				value: 'fFastLoginProcess',
				default: false,
				width: 150,
				fixWidth: true,
				custom: { yesNo: true, changeValue: true }
			},
			{
				name: 'Red Dot Alert',
				value: 'fRedDotAlert',
				default: false,
				width: 150,
				fixWidth: true,
				custom: { yesNo: true, changeValue: true }
			},
			{
				name: 'Registered',
				value: 'fRegistered',
				default: false,
				width: 150,
				fixWidth: true,
				custom: { yesNo: true, changeValue: true }
			},
			{
				name: 'Registration time',
				value: 'fCreatedTs',
				default: true,
				width: 150,
				fixWidth: true,
				custom: { date: true, changeValue: true }
			},
			{
				name: 'Registration Origin',
				value: 'fOrigin',
				default: true,
				width: 100,
				fixWidth: true
			},
			{
				name: 'Notes',
				value: 'fNotes',
				default: true,
				width: 150,
				fixWidth: true
			},
			{
				name: 'Action',
				value: 'action',
				default: true,
				width: 175,
				fixWidth: true,
				custom: { right: true }
			}
		];
	}
}